import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  // Auto-verify if code is in URL
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    const emailFromUrl = searchParams.get('email');
    
    if (codeFromUrl && emailFromUrl) {
      setEmail(emailFromUrl);
      setCode(codeFromUrl);
      // Auto-submit
      handleVerify(emailFromUrl, codeFromUrl);
    }
  }, [searchParams]);

  const handleVerify = async (emailToVerify = email, codeToVerify = code) => {
    if (!emailToVerify || !codeToVerify) {
      setError('Please enter both email and verification code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const { isSignUpComplete } = await confirmSignUp({
        username: emailToVerify,
        confirmationCode: codeToVerify
      });

      if (isSignUpComplete) {
        setSuccess('✅ Email verified successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              email: emailToVerify,
              message: 'Account verified! Please sign in.' 
            } 
          });
        }, 2000);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to verify email. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setResending(true);
      setError('');
      
      await resendSignUpCode({
        username: email
      });

      setSuccess('✅ Verification code resent! Check your email.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Resend error:', err);
      setError(err.message || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1 className="text-title" style={{ textAlign: 'center', marginBottom: '8px' }}>📧 Verify Your Email</h1>

        <p className="text-secondary" style={{ textAlign: 'center', marginBottom: '16px' }}>
          We've sent a verification code to your email address. Please enter it below to complete your registration.
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              className="form-control"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              placeholder="Enter 6-digit code"
              maxLength={6}
              style={{ letterSpacing: '2px', textAlign: 'center' }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%', marginBottom: '12px' }}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleResendCode}
            disabled={resending || !email}
            style={{ width: '100%' }}
          >
            {resending ? 'Resending...' : 'Resend Verification Code'}
          </button>
        </form>

        <div className="auth-footer">
          <p className="text-secondary">
            Already verified?{' '}
            <a href="/login">Sign in here</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;






